import { useAtomValue } from 'jotai';
import {
    ArrowUpRightIcon,
    BookIcon,
    Button,
    ButtonSize,
    ButtonVariant,
    CircleDashedIcon,
    GiftIcon,
    H6Emphasized,
    HomeIcon,
    type IconType,
    PlaneIcon,
    ReceiptTextIcon,
    ShieldPlusIcon,
    TileButton,
    UmbrellaIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import type { ContainerComponent } from '../../../common/types/Common';
import { BACKDOOR_ROTH_ENABLED_IN_GROVE } from '../../../config/FruitfulEnvironment';
import { selectedMembershipAtom } from '../../../entities/entityAtoms';
import { MembershipType } from '../../../entities/entityTypes';
import { GoalPreset, GoalPresetLabel } from '../../types/GoalPresets';
import { getBackdoorRothPresetLabel } from '../../utils/backdoorRothFormShared';
import FormDrawer from './FormDrawer';

interface GoalPresetTileButtonProps {
    text: string;
    icon: IconType;
    onPress: () => void;
    disabled?: boolean;
}

const GoalPresetTileButton = ({
    text,
    icon,
    onPress,
    disabled,
}: GoalPresetTileButtonProps) => {
    return (
        <TileButton
            text={text}
            icon={icon}
            tileProps={{
                padding: 16,
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                maxWidth: '50%',
                disabled,
            }}
            textProps={{ textAlign: 'left' }}
            onPress={onPress}
        />
    );
};

const GoalPresetButtonRow: ContainerComponent = ({ children }) => {
    return (
        <XStack justifyContent="space-between" gap={8} height={80}>
            {children}
        </XStack>
    );
};

interface GoalPresetSelectionProps {
    onPressCancel: () => void;
    onPressNext: (preset: GoalPreset) => void;
    onClose?: () => void;
    presentation?: 'drawer' | 'modal';
}

const GoalPresetSelection = ({
    onPressCancel,
    onPressNext,
    onClose,
    presentation,
}: GoalPresetSelectionProps) => {
    const membership = useAtomValue(selectedMembershipAtom);
    // Joint memberships get a second Backdoor Roth preset for the partner (users[1]).
    const isJointWithPartner =
        membership?.membershipType === MembershipType.JOINT &&
        (membership?.users?.length ?? 0) > 1;

    return (
        <FormDrawer onClose={onClose} presentation={presentation}>
            <YStack gap={16}>
                <XStack alignItems="center" justifyContent="space-between">
                    <View flexGrow={1}>
                        <H6Emphasized>Add a goal</H6Emphasized>
                    </View>
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.TINY}
                        width="auto"
                        text="Cancel"
                        onPress={() => {
                            onPressCancel();
                        }}
                    />
                </XStack>
                <YStack gap={8}>
                    <GoalPresetButtonRow>
                        <GoalPresetTileButton
                            text={
                                GoalPresetLabel[
                                    GoalPreset.ONE_MONTH_EMERGENCY_FUND
                                ]
                            }
                            icon={UmbrellaIcon}
                            onPress={() => {
                                onPressNext(
                                    GoalPreset.ONE_MONTH_EMERGENCY_FUND,
                                );
                            }}
                        />
                        <GoalPresetTileButton
                            text={
                                GoalPresetLabel[
                                    GoalPreset.FULLY_FUND_EMERGENCY_FUND
                                ]
                            }
                            icon={UmbrellaIcon}
                            onPress={() => {
                                onPressNext(
                                    GoalPreset.FULLY_FUND_EMERGENCY_FUND,
                                );
                            }}
                        />
                    </GoalPresetButtonRow>
                    <GoalPresetButtonRow>
                        <GoalPresetTileButton
                            text={GoalPresetLabel[GoalPreset.PAY_OFF_DEBT]}
                            icon={ReceiptTextIcon}
                            onPress={() => {
                                onPressNext(GoalPreset.PAY_OFF_DEBT);
                            }}
                        />
                    </GoalPresetButtonRow>
                    <GoalPresetButtonRow>
                        <GoalPresetTileButton
                            text={
                                GoalPresetLabel[
                                    GoalPreset.APEX_BASELINE_RETIREMENT
                                ]
                            }
                            icon={ShieldPlusIcon}
                            onPress={() => {
                                onPressNext(
                                    GoalPreset.APEX_BASELINE_RETIREMENT,
                                );
                            }}
                        />
                        {BACKDOOR_ROTH_ENABLED_IN_GROVE && (
                            <GoalPresetTileButton
                                text={
                                    isJointWithPartner
                                        ? getBackdoorRothPresetLabel(
                                              membership,
                                              0,
                                          )
                                        : GoalPresetLabel[
                                              GoalPreset.BACKDOOR_ROTH
                                          ]
                                }
                                icon={ArrowUpRightIcon}
                                onPress={() => {
                                    onPressNext(GoalPreset.BACKDOOR_ROTH);
                                }}
                            />
                        )}
                    </GoalPresetButtonRow>
                    {BACKDOOR_ROTH_ENABLED_IN_GROVE && isJointWithPartner && (
                        <GoalPresetButtonRow>
                            <GoalPresetTileButton
                                text={getBackdoorRothPresetLabel(membership, 1)}
                                icon={ArrowUpRightIcon}
                                onPress={() => {
                                    onPressNext(
                                        GoalPreset.BACKDOOR_ROTH_PARTNER,
                                    );
                                }}
                            />
                        </GoalPresetButtonRow>
                    )}
                    <GoalPresetButtonRow>
                        <GoalPresetTileButton
                            text={
                                GoalPresetLabel[
                                    GoalPreset.SAVE_FOR_KIDS_COLLEGE
                                ]
                            }
                            icon={BookIcon}
                            onPress={() => {
                                onPressNext(GoalPreset.SAVE_FOR_KIDS_COLLEGE);
                            }}
                        />
                        <GoalPresetTileButton
                            text={GoalPresetLabel[GoalPreset.SAVE_FOR_HOUSE]}
                            icon={HomeIcon}
                            onPress={() => {
                                onPressNext(GoalPreset.SAVE_FOR_HOUSE);
                            }}
                        />
                    </GoalPresetButtonRow>
                    <GoalPresetButtonRow>
                        <GoalPresetTileButton
                            text={GoalPresetLabel[GoalPreset.SAVE_FOR_TRAVEL]}
                            icon={PlaneIcon}
                            onPress={() => {
                                onPressNext(GoalPreset.SAVE_FOR_TRAVEL);
                            }}
                        />
                        <GoalPresetTileButton
                            text={GoalPresetLabel[GoalPreset.SAVE_FOR_GIFTS]}
                            icon={GiftIcon}
                            onPress={() => {
                                onPressNext(GoalPreset.SAVE_FOR_GIFTS);
                            }}
                        />
                    </GoalPresetButtonRow>
                    <GoalPresetButtonRow>
                        <GoalPresetTileButton
                            text={GoalPresetLabel[GoalPreset.CUSTOM]}
                            icon={CircleDashedIcon}
                            onPress={() => {
                                onPressNext(GoalPreset.CUSTOM);
                            }}
                        />
                    </GoalPresetButtonRow>
                </YStack>
            </YStack>
        </FormDrawer>
    );
};

export default GoalPresetSelection;
